import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export type PendingAssistantCopy = {
  callbackWait: string;
  thinking: string;
  working: string;
};

// This pure feature-level table is intentional: the pending ownership resolver
// accepts labels as data and stays usable in unit tests without a next-intl
// provider. `satisfies` keeps every supported locale compile-time exhaustive.
const COPY_BY_LOCALE = {
  en: {
    callbackWait: 'Waiting for {subject}...',
    thinking: 'IMAI is thinking...',
    working: 'IMAI is working...',
  },
  'pt-BR': {
    callbackWait: 'Esperando {subject}...',
    thinking: 'O IMAI est\u00E1 pensando...',
    working: 'O IMAI est\u00E1 trabalhando...',
  },
  ar: {
    callbackWait: 'في انتظار {subject}...',
    thinking: 'IMAI يفكر...',
    working: 'IMAI يعمل...',
  },
  es: {
    callbackWait: 'Esperando {subject}...',
    thinking: 'IMAI está pensando...',
    working: 'IMAI está trabajando...',
  },
  fr: {
    callbackWait: 'En attente de {subject}...',
    thinking: 'IMAI réfléchit...',
    working: 'IMAI travaille...',
  },
  it: {
    callbackWait: 'In attesa di {subject}...',
    thinking: 'IMAI sta pensando...',
    working: 'IMAI sta lavorando...',
  },
  ko: {
    callbackWait: '{subject} 대기 중...',
    thinking: 'IMAI가 생각 중입니다...',
    working: 'IMAI가 작업 중입니다...',
  },
  de: {
    callbackWait: 'Warten auf {subject}...',
    thinking: 'IMAI denkt nach...',
    working: 'IMAI arbeitet...',
  },
  ja: {
    callbackWait: '{subject}を待っています...',
    thinking: 'IMAIが考えています...',
    working: 'IMAIが作業しています...',
  },
  hi: {
    callbackWait: '{subject} की प्रतीक्षा हो रही है...',
    thinking: 'IMAI सोच रहा है...',
    working: 'IMAI काम कर रहा है...',
  },
  ru: {
    callbackWait: 'Ожидание: {subject}...',
    thinking: 'IMAI думает...',
    working: 'IMAI работает...',
  },
  zh: {
    callbackWait: '正在等待{subject}...',
    thinking: 'IMAI 正在思考...',
    working: 'IMAI 正在工作...',
  },
  'zh-TW': {
    callbackWait: '正在等待{subject}...',
    thinking: 'IMAI 正在思考...',
    working: 'IMAI 正在工作...',
  },
  vi: {
    callbackWait: 'Đang chờ {subject}...',
    thinking: 'IMAI đang suy nghĩ...',
    working: 'IMAI đang làm việc...',
  },
  id: {
    callbackWait: 'Menunggu {subject}...',
    thinking: 'IMAI sedang berpikir...',
    working: 'IMAI sedang bekerja...',
  },
} satisfies Record<SupportedLocale, PendingAssistantCopy>;

export function getPendingAssistantCopy(
  locale: string | null | undefined
): PendingAssistantCopy {
  return COPY_BY_LOCALE[normalizeLocale(locale)];
}

const genericPendingLabels = new Set<string>(
  Object.values(COPY_BY_LOCALE).flatMap(({ thinking, working }) => [
    thinking,
    working,
  ])
);

/** Recognizes only our translated generic copy, preserving specific progress. */
export function isGenericPendingAssistantLabel(label: string): boolean {
  return genericPendingLabels.has(label.trim());
}

export function formatCallbackWaitLabel(
  locale: string | null | undefined,
  subject: string
): string {
  return getPendingAssistantCopy(locale).callbackWait.replace(
    '{subject}',
    subject
  );
}
